import React, { useEffect, useState } from 'react';
import { regionsFor, defaultRegionFor, reconcileRegion } from '../lib/vpsRegions';
import { getHeaders, fetchLiveProductCatalog } from '../api';
import { createAccountCart, createProspectInvite } from '../api';
import { sendOutcome, linkFor } from '../lib/prospectInvites';
import { parseBulkInput, rowsFromBulkCheck, BULK_CHECK_PATH, BULK_CHECK_MAX } from '../lib/bulkDomainCheck';
import { quoteEnterpriseDeal, draftEnterpriseInvoice, sendEnterpriseInvoice } from '../api';
import { listAccountProducts } from '../api';
import { buildsAwaitingBalance, customWebsiteLines, withCustomWebsite, withoutLine, customWebsiteBlocker } from '../lib/customWebsite';
import ProfessionalServicesSection from '../components/ProfessionalServicesSection';
import EmailChangeSection from '../components/EmailChangeSection';
import EmailSeatsSection from '../components/EmailSeatsSection';
import { addonProductCode } from '../lib/productCodes';
import { ridesNewPlanLine } from '../lib/cartOwnedAddon';
import RenewalSection from '../components/cartBuilder/RenewalSection';
import AiVisibilitySection from '../components/cartBuilder/AiVisibilitySection';
import OwnedAddonSection from '../components/cartBuilder/OwnedAddonSection';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

// Cart builder — catalog pane is fed from the CANONICAL live catalogs
// (GET /api/products/live → squish-backend lib/*-catalog.js), not Salesforce
// Product2 (2026-07-18 rebuild; the 2026-07-17 audit found Product2 had zero
// canonical SKUs and stale prices). Lines are emitted in the shapes
// checkout-pricing + cart-fulfillment understand:
//   hosting tier   → WEB_HOSTING / MANAGED_CLOUD_<TIER> + metadata.sku/termMonths
//   hosting add-on → HOSTING_ADDON_<X> + metadata.sku/termMonths (term follows
//                    the VPS line it attaches to)
//   email          → single EMAIL line + metadata { sku, seats, billingTerm, addons }
//   domains        → DOMAIN:<domain> sub-flow lines (server normalizes at
//                    payment-intent time — lib/cart-reprice.js)
// Prices shown here are display estimates from the same catalogs; the charge
// is always re-derived server-side at checkout (fail-closed), so a stale
// modal can never mis-charge.

function fmt(n) {
  return `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const YEAR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// VPS region picker: only regions that can build the tier, from squish-backend
// via /api/products/live `hostingRegions` (src/lib/vpsRegions.js). The static
// list offered San Francisco for a Nook, which DO doesn't sell there; the
// customer was charged and refunded (2026-10-23).

// Canonical productCode for a hosting-catalog sku — the exact inverse of
// checkout-pricing's skuFromProductCode. hosting-web is the one special case.
function tierProductCode(sku) {
  if (sku === 'hosting-web') return 'WEB_HOSTING';
  return sku.replace(/^hosting-/, '').toUpperCase().replace(/-/g, '_');
}
// addonProductCode (hosting-addon-x → HOSTING_ADDON_X) lives in
// src/lib/productCodes.js: the owned-plan add-on section writes the same code.

// Due-today estimate for a hosting-family recurring price at a term.
// Mirrors lib/checkout-pricing hostingTermCents: prepaid term bills the whole
// term at the billing-terms rateFactor; no term = one month at sticker.
function hostingDueToday(monthly, termMonths, rateFactor, oneTime = 0) {
  const m = Number(monthly) || 0;
  const recurring = termMonths
    ? m * (rateFactor?.[termMonths] ?? 1) * termMonths
    : m;
  return recurring + (Number(oneTime) || 0);
}

// Due-today estimate for the EMAIL line. Mirrors deriveLineCents: annual
// bills 12 months upfront; add-ons default to the parent seat count.
function emailDueToday(entry, seats, billingTerm, addons, emailAddonCatalog) {
  const annual = billingTerm === 'annual';
  const unit = annual ? entry.annual : entry.monthly;
  let monthlyTotal = (Number(unit) || 0) * seats;
  for (const a of addons || []) {
    const ae = emailAddonCatalog?.[a.sku];
    if (!ae) continue;
    const aUnit = annual ? ae.annual : ae.monthly;
    monthlyTotal += (Number(aUnit) || 0) * (a.seats || seats);
  }
  return annual ? monthlyTotal * 12 : monthlyTotal;
}

// Domain line-item builder for ONE domain+action pair. Shared by single and
// bulk flows so they produce identical cart items. (Server-side
// lib/cart-reprice.js normalizes these into canonical DOMAIN shapes.)
function buildDomainLine({ domain, available, price, transferPrice }, action, { years = 1, eppCode = '' } = {}) {
  if (action === 'register') {
    if (!available) return null;
    const p = Number(price) || 0;
    return {
      lineId: `DOMAIN:${domain}`,
      productCode: `DOMAIN:${domain}`,
      productName: `Domain: ${domain} (${years} yr${years > 1 ? 's' : ''})`,
      quantity: 1,
      unitPrice: p * years,
      metadata: { action: 'register', domain, years, perYearPrice: p },
    };
  }
  if (action === 'transfer') {
    if (transferPrice == null) return null;
    // The auth code is optional. Without it the customer pays for the transfer
    // and squish-backend records it awaiting the code, which staff then add
    // from the customer's Transfer attempts panel (or the customer adds from
    // their dashboard).
    const ep = eppCode.trim();
    const p = Number(transferPrice) || 0;
    return {
      lineId: `DOMAIN_TRANSFER:${domain}`,
      productCode: `DOMAIN_TRANSFER:${domain}`,
      productName: `Transfer: ${domain} (+1 yr)`,
      quantity: 1,
      unitPrice: p,
      metadata: { action: 'transfer', domain, perYearPrice: p, ...(ep ? { eppCode: ep } : {}) },
    };
  }
  return null;
}

// Bulk-paste UI: one domain per line, check all in one shot, then per-row
// register/transfer/skip + optional EPP capture. Calls onAddItems with the
// fully-built cart lines.
function BulkDomainSection({ onAddItems }) {
  const [text, setText]     = useState('');
  const [rows, setRows]     = useState([]);   // { domain, available, price, transferPrice, action, years, eppCode }
  const [checking, setChecking] = useState(false);
  const [error, setError]   = useState('');
  const [adding, setAdding] = useState(false);

  async function checkAll() {
    setError(''); setRows([]);
    const valid = parseBulkInput(text);
    if (valid.length === 0) { setError('Paste at least one domain (one per line)'); return; }
    if (valid.length > BULK_CHECK_MAX) { setError(`Max ${BULK_CHECK_MAX} domains per bulk check`); return; }
    setChecking(true);
    try {
      const res = await fetch(`${API}${BULK_CHECK_PATH}?domains=${encodeURIComponent(valid.join(','))}`, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Bulk check failed');
      // One row per domain in the single check's shape — see lib/bulkDomainCheck.js.
      setRows(rowsFromBulkCheck(data));
    } catch (e) {
      setError(e.message);
    } finally {
      setChecking(false);
    }
  }

  function updateRow(idx, patch) {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  }

  function handleAddAll() {
    setAdding(true);
    try {
      const lines = rows.map(r => buildDomainLine(r, r.action, { years: r.years, eppCode: r.eppCode })).filter(Boolean);
      if (lines.length === 0) { setError('Nothing ready to add — pick an action for at least one domain'); setAdding(false); return; }
      onAddItems(lines);
      // Clear + collapse.
      setText(''); setRows([]); setError('');
    } finally {
      setAdding(false);
    }
  }

  const readyCount = rows.filter(r => buildDomainLine(r, r.action, { years: r.years, eppCode: r.eppCode })).length;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg3)', marginBottom: 14 }}>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'example.com\nanotherone.net\nthirdone.ai'}
        rows={3}
        style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button onClick={checkAll} disabled={checking || !text.trim()} style={{ ...addBtn, padding: '6px 14px', opacity: checking || !text.trim() ? 0.5 : 1 }}>
          {checking ? 'Checking…' : 'Check all'}
        </button>
        <div style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>Max {BULK_CHECK_MAX} · one per line</div>
      </div>
      {error && <div style={{ fontSize: 11, color: '#f87171', marginTop: 6 }}>⚠ {error}</div>}

      {rows.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
          {rows.map((r, i) => {
            const canReg  = r.available;
            const canXfer = r.transferPrice != null;
            const disabled = !canReg && !canXfer;
            return (
              <div key={r.domain} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 8, background: 'var(--bg)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>
                    {r.domain}
                  </span>
                  <span style={{ fontSize: 10, color: canReg ? '#4ade80' : canXfer ? '#818cf8' : 'var(--text3)', fontFamily: 'var(--mono)' }}>
                    {canReg ? `available · ${fmt(r.price)}/yr` : canXfer ? `taken · transfer ${fmt(r.transferPrice)}` : 'taken · no transfer'}
                  </span>
                </div>
                {!disabled && (
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      value={r.action}
                      onChange={e => updateRow(i, { action: e.target.value })}
                      style={miniSelect}
                    >
                      {canReg  && <option value="register">Register</option>}
                      {canXfer && <option value="transfer">Transfer</option>}
                      <option value="skip">Skip</option>
                    </select>
                    {r.action === 'register' && (
                      <select
                        value={r.years}
                        onChange={e => updateRow(i, { years: parseInt(e.target.value, 10) })}
                        style={miniSelect}
                      >
                        {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}yr</option>)}
                      </select>
                    )}
                    {r.action === 'transfer' && (
                      <input
                        value={r.eppCode}
                        onChange={e => updateRow(i, { eppCode: e.target.value })}
                        placeholder="EPP / auth code (or add after purchase)"
                        style={{ flex: 1, minWidth: 120, padding: '4px 6px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 11, fontFamily: 'var(--mono)' }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {rows.length > 0 && (
        <button
          onClick={handleAddAll}
          disabled={adding || readyCount === 0}
          style={{ ...addBtn, width: '100%', marginTop: 8, opacity: readyCount === 0 ? 0.5 : 1 }}
        >
          + Add {readyCount} to cart
        </button>
      )}
    </div>
  );
}

export default function CartBuilderModal({ accountId, accountName, prospect, onClose, onSaved }) {
  // Prospect-mode = no Salesforce account yet; cart is tied to a lead's email.
  const isProspect = !!prospect;
  // Single vs bulk domain input mode.
  const [domainBulk, setDomainBulk] = useState(false);
  // { hosting, hostingAddons, email, emailAddons, billingTerms } keyed maps.
  const [catalog, setCatalog] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]); // { lineId, productCode, productName, quantity, unitPrice, metadata }
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Prospect mode ends in an invite, not a draft: squish-backend creates the
  // customer's account with this cart in it and emails them the link. The
  // result replaces the builder so the rep sees what actually went out.
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Domain sub-flow state (variable-priced — doesn't fit the catalog list shape).
  const [domainInput, setDomainInput] = useState('');
  const [domainYears, setDomainYears] = useState(1);
  const [domainChecking, setDomainChecking] = useState(false);
  const [domainResult, setDomainResult] = useState(null); // { domain, available, price, transferPrice }
  const [domainError, setDomainError] = useState('');
  const [domainMode, setDomainMode] = useState('register'); // 'register' | 'transfer'
  const [eppCode, setEppCode] = useState('');

  // ── Payment mode ──────────────────────────────────────────────────────────
  // 'card'    — the existing flow: save a cart, rep takes a card in RepCheckout.
  // 'invoice' — sales-led deals. Unblocks selfServe:false email SKUs (E3/E5),
  //             collects PO + net terms, and bills by Stripe invoice instead.
  //             Nothing here can charge a card, and nothing SENDS until the rep
  //             clicks send on the draft.
  const [payMode, setPayMode] = useState('card');
  const [poNumber, setPoNumber] = useState('');
  const [netDays, setNetDays] = useState(30);
  const [termMonths, setTermMonths] = useState(12);
  // sku → negotiated per-seat-per-month dollars. Absent = quote at list.
  const [priceOverrides, setPriceOverrides] = useState({});
  const [quote, setQuote] = useState(null);      // { ok, lines[], totalCents, errors[] }
  const [quoting, setQuoting] = useState(false);
  const [draft, setDraft] = useState(null);      // { invoiceId, ... } once created
  const [sent, setSent] = useState(null);        // { hostedInvoiceUrl, ... } once emailed
  const [invoiceEmail, setInvoiceEmail] = useState(prospect?.email || '');
  const isInvoice = payMode === 'invoice';

  useEffect(() => {
    fetchLiveProductCatalog()
      .then(d => setCatalog(d))
      .catch(() => setError('Failed to load catalog'))
      .finally(() => setLoadingProducts(false));
  }, []);

  const hostingCat      = catalog?.hosting || {};
  const hostingAddonCat = catalog?.hostingAddons || {};
  const emailCat        = catalog?.email || {};
  const emailAddonCat   = catalog?.emailAddons || {};
  const rateFactor      = catalog?.billingTerms?.hosting?.rateFactor || {};
  const termLabels      = catalog?.billingTerms?.hosting?.label || {};
  const regionsBySku    = catalog?.hostingRegions || null;
  const cwCat           = catalog?.customWebsite || null;

  // ── Custom Website (sales-led build, staff carts only) ───────────────────
  // Deposit: pick a package; the first-month-included Nook rides along unless
  // the client already has hosting. Balance: pick the account's build in
  // progress; its package sets the price and its id goes on the line, so
  // paying it delivers THAT build (src/lib/customWebsite.js).
  const [cwPhase, setCwPhase] = useState('deposit');
  const [cwSku, setCwSku] = useState('');
  const [cwNook, setCwNook] = useState(true);
  const [cwBuilds, setCwBuilds] = useState(null); // null loading | { error } | builds[]
  const [cwBuildId, setCwBuildId] = useState('');
  const cwSkus = Object.keys(cwCat?.tiers || {});
  const cwSelectedSku = cwSkus.includes(cwSku) ? cwSku : cwSkus[0] || '';
  const cwBuild = Array.isArray(cwBuilds) ? (cwBuilds.find(b => b.productId === cwBuildId) || cwBuilds[0] || null) : null;
  const cwBlocker = customWebsiteBlocker({ cwCatalog: cwCat, isInvoice, isProspect, phase: cwPhase, builds: cwBuilds });
  const cwLines = cwBlocker ? null : customWebsiteLines(cwCat, { phase: cwPhase, sku: cwSelectedSku, includeNook: cwNook, engagement: cwBuild });

  useEffect(() => {
    if (cwPhase !== 'balance' || isProspect || !accountId || cwBuilds != null) return;
    let cancelled = false;
    listAccountProducts(accountId)
      .then(r => {
        if (cancelled) return;
        const builds = buildsAwaitingBalance(r.products);
        // A short list (one adapter failed) is not proof there is no build.
        setCwBuilds(builds.length === 0 && r.productsComplete === false
          ? { error: 'some of their products could not be read' }
          : builds);
      })
      .catch(e => { if (!cancelled) setCwBuilds({ error: e.message || 'request failed' }); });
    return () => { cancelled = true; };
  }, [cwPhase, isProspect, accountId, cwBuilds]);

  function addCustomWebsite() {
    if (!cwLines) return;
    setItems(prev => withCustomWebsite(prev, cwLines));
  }

  // When the availability answer arrives, move any VPS line off a region that
  // can't build its tier (a line added before the catalog finished loading).
  useEffect(() => {
    if (!regionsBySku) return;
    setItems(prev => {
      let changed = false;
      const next = prev.map(i => {
        if (!i.metadata?.vpsRegion || !i.metadata?.sku) return i;
        const region = reconcileRegion(i.metadata.sku, i.metadata.vpsRegion, regionsBySku);
        if (region === i.metadata.vpsRegion) return i;
        changed = true;
        return { ...i, metadata: { ...i.metadata, vpsRegion: region } };
      });
      return changed ? next : prev;
    });
  }, [regionsBySku]);

  async function checkDomain() {
    setDomainError('');
    setDomainResult(null);
    setEppCode('');
    const d = domainInput.trim().toLowerCase();
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d)) {
      setDomainError('Enter a full domain (e.g. example.com)');
      return;
    }
    setDomainChecking(true);
    try {
      const res = await fetch(`${API}/api/domain-check?domain=${encodeURIComponent(d)}`, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Check failed');
      setDomainResult(data);
      // Auto-select the mode the rep is most likely to want:
      //   - available    → default to register
      //   - not available → default to transfer (the whole reason to offer it)
      setDomainMode(data.available ? 'register' : 'transfer');
    } catch (e) {
      setDomainError(e.message);
    }
    setDomainChecking(false);
  }

  // Upsert one or more lines by lineId. Shared between the single-domain flow
  // and the bulk-paste section.
  function upsertLines(lines) {
    if (!Array.isArray(lines) || lines.length === 0) return;
    setItems(prev => {
      const byId = new Map(prev.map(i => [i.lineId, i]));
      for (const l of lines) byId.set(l.lineId, l);
      return Array.from(byId.values());
    });
  }

  function addDomainToCart() {
    if (!domainResult) return;
    // No EPP check: a transfer can be sold before the customer has the code
    // (see buildDomainLine).
    if (domainMode === 'transfer' && domainResult.transferPrice == null) {
      setDomainError('Transfer pricing unavailable for this TLD');
      return;
    }
    const line = buildDomainLine(domainResult, domainMode, {
      years: domainYears,
      eppCode: eppCode.trim(),
    });
    if (!line) return;
    upsertLines([line]);

    // Reset mini-form so rep can add another quickly.
    setDomainInput('');
    setDomainResult(null);
    setDomainYears(1);
    setEppCode('');
    setDomainError('');
  }

  // ── Hosting tiers ─────────────────────────────────────────────────────────
  function addHostingTier(entry) {
    if (items.some(i => i.lineId === entry.sku)) return;
    upsertLines([{
      lineId: entry.sku,
      productCode: tierProductCode(entry.sku),
      productName: entry.label,
      quantity: 1,
      unitPrice: hostingDueToday(entry.monthly, null, rateFactor),
      metadata: {
        sku: entry.sku,
        termMonths: null,
        // Droplet region for VPS tiers — fulfillment reads metadata.vpsRegion.
        // Starts on a region that can build this tier (src/lib/vpsRegions.js).
        ...(entry.category === 'vps' ? { vpsRegion: defaultRegionFor(entry.sku, regionsBySku) } : {}),
      },
    }]);
  }

  function setTierRegion(lineId, vpsRegion) {
    setItems(prev => prev.map(i =>
      i.lineId === lineId ? { ...i, metadata: { ...i.metadata, vpsRegion } } : i
    ));
  }

  // Term change on a tier propagates to attached HOSTING_ADDON lines so the
  // add-on's prepaid window always matches its parent (fulfillment prices +
  // schedules them off the same termMonths).
  function setTierTerm(lineId, termMonths) {
    setItems(prev => {
      const line = prev.find(i => i.lineId === lineId);
      const isVps = line && hostingCat[line.metadata?.sku]?.category === 'vps';
      return prev.map(i => {
        if (i.lineId === lineId) {
          const entry = hostingCat[i.metadata.sku];
          return {
            ...i,
            unitPrice: hostingDueToday(entry?.monthly, termMonths, rateFactor),
            metadata: { ...i.metadata, termMonths },
          };
        }
        // ridesNewPlanLine: not an add-on for a plan they already have, which
        // squish-backend priced and whose term is its own plan's.
        if (isVps && ridesNewPlanLine(i)) {
          const ae = hostingAddonCat[i.metadata.sku];
          return {
            ...i,
            unitPrice: hostingDueToday(ae?.monthly, termMonths, rateFactor, ae?.oneTime),
            metadata: { ...i.metadata, termMonths },
          };
        }
        return i;
      });
    });
  }

  // ── Hosting add-ons ───────────────────────────────────────────────────────
  // An add-on needs a compatible hosting line in the cart to attach to
  // (fulfillment rides add-ons on the tier's provisioning pass).
  function hostingLines() {
    return items.filter(i => i.metadata?.sku && hostingCat[i.metadata.sku]);
  }
  function addonAttachTarget(entry) {
    const lines = hostingLines();
    if (entry.appliesToCategories?.includes('*')) return lines[0] || null;
    return lines.find(l => entry.appliesToCategories?.includes(hostingCat[l.metadata.sku]?.category)) || null;
  }
  function addHostingAddon(entry) {
    if (items.some(i => i.lineId === entry.sku)) return;
    const target = addonAttachTarget(entry);
    if (!target) return;
    const termMonths = target.metadata?.termMonths ?? null;
    upsertLines([{
      lineId: entry.sku,
      productCode: addonProductCode(entry.sku),
      productName: entry.label,
      quantity: 1,
      unitPrice: hostingDueToday(entry.monthly, termMonths, rateFactor, entry.oneTime),
      metadata: { sku: entry.sku, termMonths },
    }]);
  }

  // ── Email (single EMAIL line; add-ons nest inside it) ────────────────────
  const emailLine = items.find(i => i.productCode === 'EMAIL') || null;

  function recomputeEmailPrice(meta) {
    const entry = emailCat[meta.sku];
    return emailDueToday(entry, meta.seats, meta.billingTerm, meta.addons, emailAddonCat);
  }
  function addEmailTier(entry) {
    if (emailLine) return; // one email line per cart — remove it to switch tiers
    const metadata = { sku: entry.sku, seats: 1, billingTerm: 'monthly', addons: [] };
    upsertLines([{
      lineId: 'EMAIL',
      productCode: 'EMAIL',
      productName: `Email — ${entry.label}`,
      quantity: 1,
      unitPrice: emailDueToday(entry, 1, 'monthly', [], emailAddonCat),
      metadata,
    }]);
  }
  function updateEmailLine(patch) {
    setItems(prev => prev.map(i => {
      if (i.productCode !== 'EMAIL') return i;
      const metadata = { ...i.metadata, ...patch };
      return { ...i, metadata, unitPrice: recomputeEmailPrice(metadata) };
    }));
  }
  function addEmailAddon(entry) {
    if (!emailLine) return;
    if ((emailLine.metadata.addons || []).some(a => a.sku === entry.sku)) return;
    updateEmailLine({ addons: [...(emailLine.metadata.addons || []), { sku: entry.sku }] });
  }
  function removeEmailAddon(sku) {
    updateEmailLine({ addons: (emailLine?.metadata.addons || []).filter(a => a.sku !== sku) });
  }

  function removeItem(lineId) {
    // withoutLine: removing a Custom Website build takes its bundled Nook too.
    setItems(withoutLine(items, lineId));
  }

  const subtotal = items.reduce((s, i) => s + i.unitPrice, 0);

  // ── Invoice mode: cart items → enterprise-invoice lines ───────────────────
  // Only per-seat EMAIL SKUs map cleanly: they have a seat count and a verified
  // per-seat cost basis, which is what the margin floor is checked against.
  // Hosting and domains are excluded rather than guessed at — a hosting line has
  // no seat concept, and quoting one at a made-up per-seat price would either be
  // refused server-side or, worse, accepted at a number that means nothing.
  function invoiceLines() {
    const out = [];
    for (const i of items) {
      if (i.productCode !== 'EMAIL') continue;
      const m = i.metadata || {};
      const tier = emailCat[m.sku];
      if (!tier) continue;
      out.push({
        sku: m.sku,
        quantity: Number(m.seats) || 1,
        unitPriceMonthly: priceOverrides[m.sku] ?? (termMonths >= 12 ? tier.annual : tier.monthly),
      });
      for (const a of (m.addons || [])) {
        const ad = emailAddonCat[a.sku];
        if (!ad) continue;
        out.push({
          sku: a.sku,
          quantity: Number(a.seats || m.seats) || 1,
          unitPriceMonthly: priceOverrides[a.sku] ?? (termMonths >= 12 ? (ad.annual ?? ad.monthly) : (ad.monthly ?? ad.annual)),
        });
      }
    }
    return out;
  }

  // Non-email lines can't be invoiced through this path — say so rather than
  // silently dropping them from the total.
  const unbillableLines = isInvoice ? items.filter(i => i.productCode !== 'EMAIL') : [];

  // Re-quote whenever the deal shape changes. Debounced because the rep is
  // typing prices, and the endpoint is pure so calling it often is harmless.
  useEffect(() => {
    if (!isInvoice) { setQuote(null); return; }
    const lines = invoiceLines();
    if (lines.length === 0) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    const t = setTimeout(() => {
      quoteEnterpriseDeal({ lines, termMonths, netDays })
        .then(q => { if (!cancelled) setQuote(q); })
        .catch(e => { if (!cancelled) setQuote({ ok: false, errors: [e.message], lines: [], totalCents: 0 }); })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvoice, items, termMonths, netDays, priceOverrides]);

  async function handleCreateDraft() {
    setError('');
    setSaving(true);
    try {
      const d = await draftEnterpriseInvoice({
        customerEmail: invoiceEmail,
        customerName: accountName || prospect?.name,
        accountId: accountId || null,
        lines: invoiceLines(),
        termMonths,
        netDays,
        poNumber: poNumber || null,
        // No `description`: squish-backend sets it as the Stripe invoice's
        // description, which prints on the invoice the customer receives, and
        // `notes` is the "Internal notes" field below. Internal stays internal.
      });
      setDraft(d);
    } catch (e) {
      // The backend passes the per-line refusal reasons through on a 422.
      setError(e.message);
    }
    setSaving(false);
  }

  async function handleSendInvoice() {
    setError('');
    setSaving(true);
    try {
      setSent(await sendEnterpriseInvoice(draft.invoiceId));
      onSaved?.();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function handleSave() {
    if (items.length === 0) { setError('Add at least one item'); return; }
    setSaving(true);
    setError('');
    try {
      if (isProspect) {
        const result = await createProspectInvite({ prospect, items, notes });
        setInviteResult(result);
        onSaved?.(result);
      } else {
        const cart = await createAccountCart(accountId, { items, notes });
        onSaved?.(cart);
        onClose();
      }
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function copyInviteLink() {
    const url = linkFor(inviteResult);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard refused — the link is on screen to select by hand */ }
  }

  // ── Catalog pane data ─────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const match = (e) => !q || `${e.label || ''} ${e.sku || ''} ${e.desc || ''}`.toLowerCase().includes(q);
  const sections = [
    { title: 'Hosting', entries: Object.values(hostingCat).filter(match), kind: 'tier' },
    { title: 'Hosting add-ons', entries: Object.values(hostingAddonCat).filter(match), kind: 'hostingAddon' },
    { title: 'Email', entries: Object.values(emailCat).filter(match), kind: 'emailTier' },
    { title: 'Email add-ons', entries: Object.values(emailAddonCat).filter(match), kind: 'emailAddon' },
  ];
  const anyMatches = sections.some(s => s.entries.length > 0);

  // Why an entry can't be added right now (null = addable). Sales-led products
  // are shown for reference but can't be carted — checkout would fail closed
  // on them anyway (selfServe guard in checkout-pricing).
  function addBlocker(kind, entry) {
    // Sales-led SKUs have no self-serve Stripe price, so a CARD checkout would
    // fail closed on them (selfServe guard in checkout-pricing). Invoice mode
    // is exactly the path they were waiting for — E3/E5 and the like bill by
    // invoice with a PO and net terms — so unblock them there.
    //
    // Only EMAIL kinds: those are per-seat with a verified cost basis, which is
    // what the margin floor is checked against. A sales-led HOSTING line
    // (Managed AWS, Cloud Custom) has no seat count and no cost in the catalog,
    // so it stays blocked rather than producing a quote that means nothing.
    const invoiceable = isInvoice && (kind === 'emailTier' || kind === 'emailAddon');
    if (entry.selfServe === false && !invoiceable) return 'Sales-led';
    if (kind === 'tier' && items.some(i => i.lineId === entry.sku)) return 'In cart';
    if (kind === 'hostingAddon') {
      if (items.some(i => i.lineId === entry.sku)) return 'In cart';
      if (!addonAttachTarget(entry)) return 'Needs hosting line';
    }
    if (kind === 'emailTier' && emailLine) return emailLine.metadata.sku === entry.sku ? 'In cart' : 'Email line exists';
    if (kind === 'emailAddon') {
      if (!emailLine) return 'Needs email line';
      if ((emailLine.metadata.addons || []).some(a => a.sku === entry.sku)) return 'In cart';
    }
    return null;
  }
  function addEntry(kind, entry) {
    if (kind === 'tier') addHostingTier(entry);
    else if (kind === 'hostingAddon') addHostingAddon(entry);
    else if (kind === 'emailTier') addEmailTier(entry);
    else if (kind === 'emailAddon') addEmailAddon(entry);
  }
  function panePrice(kind, entry) {
    if (kind === 'tier' || kind === 'hostingAddon') return entry.priceDisplay || (entry.monthly != null ? `$${entry.monthly}/mo` : '—');
    return entry.monthly != null ? `$${entry.monthly}/user/mo` : '—';
  }

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
              {isProspect ? 'Build Prospect Cart' : 'Build Cart'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontFamily: 'var(--mono)' }}>
              {isProspect
                ? `for ${prospect.email}${prospect.company ? ` · ${prospect.company}` : ''} · creates their account and emails an invite`
                : `for ${accountName || accountId} · saves as draft`}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {inviteResult ? (
          <InviteSent result={inviteResult} prospect={prospect} copied={copied} onCopy={copyInviteLink} onDone={onClose} />
        ) : (<>
        <div style={body}>
          {error && <div className="error-state" style={{ marginBottom: 12 }}>⚠ {error}</div>}

          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* ── Left: catalog ── */}
            <div>
              {/* Domain registration — variable pricing, doesn't fit catalog shape */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ ...sectionLabel, margin: 0 }}>Register a Domain</div>
                <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--bg)', borderRadius: 4, border: '1px solid var(--border)' }}>
                  <button
                    onClick={() => setDomainBulk(false)}
                    style={{ padding: '3px 9px', borderRadius: 3, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600, fontFamily: 'var(--mono)', background: !domainBulk ? 'var(--accent)' : 'transparent', color: !domainBulk ? '#fff' : 'var(--text3)' }}
                  >
                    One
                  </button>
                  <button
                    onClick={() => setDomainBulk(true)}
                    style={{ padding: '3px 9px', borderRadius: 3, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600, fontFamily: 'var(--mono)', background: domainBulk ? 'var(--accent)' : 'transparent', color: domainBulk ? '#fff' : 'var(--text3)' }}
                  >
                    Bulk
                  </button>
                </div>
              </div>

              {domainBulk && (
                <BulkDomainSection onAddItems={upsertLines} />
              )}

              {!domainBulk && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg3)', marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={domainInput}
                    onChange={e => { setDomainInput(e.target.value.toLowerCase().trim()); setDomainResult(null); setDomainError(''); }}
                    onKeyDown={e => e.key === 'Enter' && checkDomain()}
                    placeholder="example.com"
                    style={{ ...input, flex: 1, fontFamily: 'var(--mono)' }}
                  />
                  <button
                    onClick={checkDomain}
                    disabled={domainChecking || !domainInput}
                    style={{ ...addBtn, padding: '6px 14px', opacity: domainChecking || !domainInput ? 0.5 : 1 }}
                  >
                    {domainChecking ? '…' : 'Check'}
                  </button>
                </div>
                {domainError && (
                  <div style={{ fontSize: 11, color: '#f87171', marginTop: 6 }}>⚠ {domainError}</div>
                )}

                {domainResult && (
                  <div style={{ marginTop: 8 }}>
                    {/* Header: status line + mode toggle (tabs) */}
                    <div style={{ fontSize: 12, color: 'var(--text)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                      {domainResult.available
                        ? <>✓ <strong>{domainResult.domain}</strong> is available</>
                        : <>✗ <strong>{domainResult.domain}</strong> is taken — transfer it if the customer owns it</>
                      }
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8, padding: 3, background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)' }}>
                      <button
                        onClick={() => setDomainMode('register')}
                        disabled={!domainResult.available}
                        style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: 'none', cursor: domainResult.available ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, background: domainMode === 'register' ? 'var(--accent)' : 'transparent', color: domainMode === 'register' ? '#fff' : (domainResult.available ? 'var(--text2)' : 'var(--text3)'), opacity: domainResult.available ? 1 : 0.4 }}
                        title={domainResult.available ? '' : 'Domain is already registered'}
                      >
                        Register
                      </button>
                      <button
                        onClick={() => setDomainMode('transfer')}
                        disabled={domainResult.transferPrice == null}
                        style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: 'none', cursor: domainResult.transferPrice != null ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, background: domainMode === 'transfer' ? 'var(--accent)' : 'transparent', color: domainMode === 'transfer' ? '#fff' : (domainResult.transferPrice != null ? 'var(--text2)' : 'var(--text3)'), opacity: domainResult.transferPrice != null ? 1 : 0.4 }}
                        title={domainResult.transferPrice == null ? 'Transfer pricing unavailable for this TLD' : 'Move this domain from another registrar'}
                      >
                        Transfer
                      </button>
                    </div>

                    {/* Register panel */}
                    {domainMode === 'register' && domainResult.available && (
                      <div style={{ padding: 8, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 6 }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                          {fmt(domainResult.price)}/yr · retail
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <select
                            value={domainYears}
                            onChange={e => setDomainYears(parseInt(e.target.value, 10))}
                            style={{ padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)' }}
                          >
                            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}yr</option>)}
                          </select>
                          <button onClick={addDomainToCart} style={{ ...addBtn, flex: 1 }}>
                            + Add · {fmt(domainResult.price * domainYears)}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Transfer panel */}
                    {domainMode === 'transfer' && domainResult.transferPrice != null && (
                      <div style={{ padding: 8, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 6 }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                          {fmt(domainResult.transferPrice)} · at-cost · includes +1 yr registration
                        </div>
                        <input
                          value={eppCode}
                          onChange={e => setEppCode(e.target.value)}
                          placeholder="EPP / auth code (optional — can be added after purchase)"
                          style={{ ...input, fontFamily: 'var(--mono)', marginBottom: 6 }}
                        />
                        <button
                          onClick={addDomainToCart}
                          style={{ ...addBtn, width: '100%' }}
                        >
                          + Add Transfer · {fmt(domainResult.transferPrice)}{eppCode.trim() ? '' : ' · code later'}
                        </button>
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6, lineHeight: 1.4 }}>
                          Customer must get the EPP/auth code from their current registrar and ensure the domain is unlocked + WHOIS privacy is disabled. ICANN rules: every transfer adds exactly 1 year.
                          {!eppCode.trim() && ' No code yet? Add it anyway: once they pay, enter the code on their account under Transfer attempts.'}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Custom Website: deposit or balance of a build */}
              {cwCat && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ ...sectionLabel, margin: 0 }}>Custom Website</div>
                    <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--bg)', borderRadius: 4, border: '1px solid var(--border)' }}>
                      {['deposit', 'balance'].map(p => (
                        <button
                          key={p}
                          onClick={() => setCwPhase(p)}
                          style={{ padding: '3px 9px', borderRadius: 3, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600, fontFamily: 'var(--mono)', textTransform: 'capitalize', background: cwPhase === p ? 'var(--accent)' : 'transparent', color: cwPhase === p ? '#fff' : 'var(--text3)' }}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg3)', marginBottom: 14 }}>
                    {cwPhase === 'deposit' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <select value={cwSelectedSku} onChange={e => setCwSku(e.target.value)} style={{ ...miniSelect, fontSize: 12, padding: '6px 8px' }}>
                          {cwSkus.map(sku => {
                            const t = cwCat.tiers[sku];
                            return <option key={sku} value={sku}>{t.label} · {fmt(t.priceDollars)} total · {fmt(t.phaseDollars.deposit)} deposit</option>;
                          })}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text2)' }}>
                          <input type="checkbox" checked={cwNook} onChange={e => setCwNook(e.target.checked)} />
                          Include their Nook, first month free (untick if the site already has hosting)
                        </label>
                      </div>
                    )}
                    {cwPhase === 'balance' && Array.isArray(cwBuilds) && cwBuilds.length > 0 && (
                      <select value={cwBuild?.productId || ''} onChange={e => setCwBuildId(e.target.value)} style={{ ...miniSelect, width: '100%', fontSize: 12, padding: '6px 8px' }}>
                        {cwBuilds.map(b => (
                          <option key={b.productId} value={b.productId}>
                            {cwCat.tiers[b.sku]?.label || b.label || b.sku}{b.createdAt ? ` · deposit ${b.createdAt.slice(0, 10)}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    {cwBlocker ? (
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: cwPhase === 'deposit' ? 6 : 0, lineHeight: 1.4 }}>
                        {cwBlocker}
                        {cwBuilds?.error && (
                          <button onClick={() => setCwBuilds(null)} style={{ ...addBtn, marginLeft: 8, background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)' }}>Retry</button>
                        )}
                      </div>
                    ) : (
                      <button onClick={addCustomWebsite} disabled={!cwLines} style={{ ...addBtn, width: '100%', marginTop: 8, opacity: cwLines ? 1 : 0.5 }}>
                        + Add {cwPhase} · {fmt((cwLines || []).reduce((s, l) => s + l.unitPrice, 0))}
                      </button>
                    )}
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6, lineHeight: 1.4 }}>
                      {cwPhase === 'deposit'
                        ? 'Half up front. Paying it starts the build. One build per cart.'
                        : 'The other half, once they approve the finished preview. Paying it marks that build delivered.'}
                    </div>
                  </div>
                </>
              )}

              {/* Managed Store build fee, Managed AI / AWS, care plans (staff carts only) */}
              <ProfessionalServicesSection
                psCatalog={catalog?.professionalServices || null}
                isInvoice={isInvoice}
                items={items}
                onAdd={line => setItems(prev => [...prev, line])}
                fmt={fmt}
                styles={{ sectionLabel, miniSelect, addBtn }}
              />

              {/* Email: upgrade or add to a subscription the account already has */}
              {!isProspect && !isInvoice && (
                <EmailChangeSection accountId={accountId} isInvoice={isInvoice} isProspect={isProspect} items={items} setItems={setItems} ui={{ sectionLabel, miniSelect, addBtn, fmt }} />
              )}

              {/* Email: more seats on a subscription the account already has */}
              {!isProspect && !isInvoice && (
                <EmailSeatsSection accountId={accountId} isInvoice={isInvoice} isProspect={isProspect} items={items} setItems={setItems} ui={{ sectionLabel, miniSelect, addBtn, fmt }} />
              )}
              {/* Priced by squish-backend when added, stored as priced (src/components/cartBuilder/) */}
              {catalog && <RenewalSection accountId={accountId} isProspect={isProspect} isInvoice={isInvoice} billingTerms={catalog.billingTerms} onAdd={upsertLines} />}
              {catalog && <AiVisibilitySection accountId={accountId} prospect={prospect} isInvoice={isInvoice} onAdd={upsertLines} />}
              {catalog && <OwnedAddonSection accountId={accountId} isProspect={isProspect} isInvoice={isInvoice} hostingAddons={catalog.hostingAddons} onAdd={upsertLines} />}

              <div style={sectionLabel}>Catalog</div>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search products…"
                style={input}
              />
              <div style={{ marginTop: 8, maxHeight: 320, overflowY: 'auto' }}>
                {loadingProducts && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 12 }}>Loading…</div>}
                {!loadingProducts && sections.map(s => s.entries.length > 0 && (
                  <div key={s.title}>
                    <div style={{ ...sectionLabel, fontSize: 10, marginTop: 8 }}>{s.title}</div>
                    {s.entries.map(e => {
                      const blocker = addBlocker(s.kind, e);
                      return (
                        <div key={e.sku} style={{ ...productRow, opacity: e.selfServe === false ? 0.55 : 1 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{e.label}</div>
                            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {e.sku} · {panePrice(s.kind, e)}
                            </div>
                          </div>
                          {blocker
                            ? <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }} title={blocker === 'Sales-led' ? 'No self-serve checkout — route through a sales inquiry' : blocker}>{blocker}</span>
                            : <button onClick={() => addEntry(s.kind, e)} style={addBtn}>+ Add</button>}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {!loadingProducts && !anyMatches && (
                  <div style={{ color: 'var(--text3)', fontSize: 12, padding: 12, textAlign: 'center' }}>
                    No products match "{search}".
                  </div>
                )}
              </div>
            </div>

            {/* ── Right: cart ── */}
            <div>
              <div style={sectionLabel}>Cart ({items.length})</div>
              {items.length === 0 && (
                <div style={emptyCart}>
                  Click + Add on a product to start.
                </div>
              )}
              {items.map(i => {
                const isTier  = !!(i.metadata?.sku && hostingCat[i.metadata.sku]);
                const isAddon = ridesNewPlanLine(i);
                const isEmail = i.productCode === 'EMAIL';
                return (
                  <div key={i.lineId} style={{ ...cartRow, flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.productName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                          {i.productCode}
                        </div>
                      </div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700, color: 'var(--accent)', minWidth: 80, textAlign: 'right' }}>
                        {fmt(i.unitPrice)}
                      </div>
                      <button onClick={() => removeItem(i.lineId)} style={removeBtn}>×</button>
                    </div>

                    {/* Hosting tier: term picker (add-ons follow their parent's term)
                        + droplet region for VPS tiers */}
                    {isTier && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select
                          value={i.metadata.termMonths ?? ''}
                          onChange={e => setTierTerm(i.lineId, e.target.value ? parseInt(e.target.value, 10) : null)}
                          style={miniSelect}
                        >
                          <option value="">Monthly</option>
                          {[12, 24, 36].map(m => (
                            <option key={m} value={m}>{termLabels[m] || `${m} mo`} prepaid</option>
                          ))}
                        </select>
                        {hostingCat[i.metadata.sku]?.category === 'vps' && (
                          regionsFor(i.metadata.sku, regionsBySku).length === 0 ? (
                            <span style={{ fontSize: 11, color: 'var(--red, #f87171)', fontFamily: 'var(--mono)' }}>
                              No region can build this tier right now
                            </span>
                          ) : (
                            <select
                              value={i.metadata.vpsRegion || defaultRegionFor(i.metadata.sku, regionsBySku)}
                              onChange={e => setTierRegion(i.lineId, e.target.value)}
                              style={miniSelect}
                              title="Datacenter regions that can build this tier right now"
                            >
                              {regionsFor(i.metadata.sku, regionsBySku).map(([slug, label]) => (
                                <option key={slug} value={slug}>{label}</option>
                              ))}
                            </select>
                          )
                        )}
                        <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                          {i.metadata.termMonths ? `bills full term upfront` : 'bills monthly'}
                        </span>
                      </div>
                    )}
                    {isAddon && (
                      <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                        term follows its hosting line{i.metadata.termMonths ? ` · ${termLabels[i.metadata.termMonths] || i.metadata.termMonths + ' mo'} prepaid` : ' · monthly'}
                      </div>
                    )}

                    {/* Email: seats + billing term + attached add-ons */}
                    {isEmail && (
                      <>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>seats</span>
                          <input
                            type="number"
                            min="1"
                            value={i.metadata.seats}
                            onChange={e => updateEmailLine({ seats: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                            style={qtyInput}
                          />
                          <select
                            value={i.metadata.billingTerm}
                            onChange={e => updateEmailLine({ billingTerm: e.target.value })}
                            style={miniSelect}
                          >
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual (12 mo upfront)</option>
                          </select>
                        </div>
                        {(i.metadata.addons || []).length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {i.metadata.addons.map(a => (
                              <span key={a.sku} style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text2)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                {emailAddonCat[a.sku]?.label || a.sku}
                                <button onClick={() => removeEmailAddon(a.sku)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, fontSize: 11, lineHeight: 1 }}>×</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {items.length > 0 && (
                <div style={subtotalRow}>
                  <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>Due today (est.)</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 700, color: 'var(--accent3)' }}>{fmt(subtotal)}</span>
                </div>
              )}
              {items.length > 0 && (
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, lineHeight: 1.4 }}>
                  Estimates from the live catalogs — checkout re-derives every price server-side and bounces if anything drifted.
                </div>
              )}

              {/* ── Invoice mode: terms + live margin quote ── */}
              {isInvoice && (
                <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg3)' }}>
                  <div style={sectionLabel}>Invoice terms</div>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <label style={miniLabel}>Term</label>
                      <select value={termMonths} onChange={e => setTermMonths(Number(e.target.value))} style={{ ...miniSelect, width: '100%' }}>
                        <option value={12}>1 year</option>
                        <option value={36}>3 years</option>
                        <option value={1}>Month-to-month</option>
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={miniLabel}>Payment terms</label>
                      <select value={netDays} onChange={e => setNetDays(Number(e.target.value))} style={{ ...miniSelect, width: '100%' }}>
                        {[15, 30, 45, 60].map(d => <option key={d} value={d}>Net {d}</option>)}
                      </select>
                    </div>
                  </div>

                  <label style={miniLabel}>Bill to (email)</label>
                  <input value={invoiceEmail} onChange={e => setInvoiceEmail(e.target.value)} placeholder="ap@customer.com" style={{ ...input, marginBottom: 8 }} />

                  <label style={miniLabel}>PO number (optional)</label>
                  <input value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="PO-4471" style={{ ...input, marginBottom: 8 }} />

                  {/* Longer terms buy cheaper from Microsoft, which is usually the
                      difference between a deal clearing the floor and being refused. */}
                  {termMonths < 36 && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', lineHeight: 1.4, marginBottom: 8 }}>
                      A 3-year term buys materially cheaper — if a price is refused here, try the longer term before discounting.
                    </div>
                  )}

                  {unbillableLines.length > 0 && (
                    <div style={warnBox}>
                      {unbillableLines.length} non-email line{unbillableLines.length === 1 ? '' : 's'} can’t be invoiced this way and {unbillableLines.length === 1 ? 'is' : 'are'} excluded from the total. Sell those on a card cart.
                    </div>
                  )}

                  <div style={{ ...sectionLabel, marginTop: 10 }}>
                    Quote {quoting && <span style={{ color: 'var(--text3)', fontWeight: 400 }}>· checking…</span>}
                  </div>

                  {!quote && !quoting && (
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Add an email tier to price the deal.</div>
                  )}

                  {quote && !quote.ok && (
                    <div style={errBox}>
                      {(quote.errors || []).map((e, n) => <div key={n} style={{ marginBottom: 4 }}>{e}</div>)}
                    </div>
                  )}

                  {quote && quote.ok && (
                    <>
                      {quote.lines.map(l => (
                        <div key={l.sku} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, color: 'var(--text)' }}>{l.label}</div>
                            <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                              {l.quantity} × ${l.unitPriceMonthly}/mo × {l.termMonths}mo
                              {l.discountVsListPct > 0 && ` · ${l.discountVsListPct}% off list`}
                            </div>
                          </div>
                          <input
                            type="number"
                            value={priceOverrides[l.sku] ?? l.unitPriceMonthly}
                            onChange={e => setPriceOverrides(p => ({ ...p, [l.sku]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                            style={{ ...qtyInput, width: 62 }}
                            title="Negotiated price per seat per month"
                          />
                          {/* Margin is INTERNAL — this modal is staff-only, and the
                              number is what stops a rep discounting into a loss. */}
                          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: l.marginPct >= 15 ? 'var(--accent3)' : 'var(--text2)', width: 46, textAlign: 'right' }}>
                            {l.marginPct}%
                          </span>
                        </div>
                      ))}
                      <div style={subtotalRow}>
                        <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>Invoice total</span>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 700, color: 'var(--accent3)' }}>
                          {fmt(quote.totalCents / 100)}
                        </span>
                      </div>
                    </>
                  )}

                  {draft && !sent && (
                    <div style={okBox}>
                      Draft <span style={{ fontFamily: 'var(--mono)' }}>{draft.invoiceId}</span> created. Nothing has been sent yet — review it in Stripe, then send.
                    </div>
                  )}
                  {sent && (
                    <div style={okBox}>
                      Invoice sent to {invoiceEmail}.{' '}
                      {sent.hostedInvoiceUrl && <a href={sent.hostedInvoiceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>View</a>}
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop: 14 }}>
                <div style={sectionLabel}>Internal notes (optional)</div>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Context for this cart — call reference, pricing override rationale, etc."
                  rows={3}
                  style={{ ...input, resize: 'vertical', fontFamily: 'var(--font)' }}
                />
              </div>
            </div>
          </div>
        </div>

        <div style={footer}>
          {/* Payment mode lives in the footer, next to the action it changes. */}
          <div style={{ marginRight: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
            {[['card', 'Card'], ['invoice', 'Invoice']].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => { setPayMode(mode); setDraft(null); setSent(null); setError(''); }}
                disabled={!!draft}
                style={mode === payMode ? modeBtnActive : modeBtn}
                title={mode === 'invoice'
                  ? 'Sales-led deals — bills by invoice with a PO and net terms, and unblocks E3/E5'
                  : 'Rep takes a card over the phone'}
              >
                {label}
              </button>
            ))}
          </div>

          <button onClick={onClose} style={btnSecondary}>{sent ? 'Close' : 'Cancel'}</button>

          {!isInvoice && (
            <button onClick={handleSave} disabled={saving || items.length === 0} style={btnPrimary}>
              {isProspect
                ? (saving ? 'Sending…' : `Create account & send invite · ${fmt(subtotal)}`)
                : (saving ? 'Saving…' : `Save Draft · ${fmt(subtotal)}`)}
            </button>
          )}

          {/* Draft and send are separate buttons, never one. Creating a draft is
              private and reversible; sending puts a demand for money in a
              customer's inbox and must be its own decision. */}
          {isInvoice && !draft && (
            <button
              onClick={handleCreateDraft}
              disabled={saving || !quote?.ok || !invoiceEmail}
              style={btnPrimary}
              title={!invoiceEmail ? 'Bill-to email required' : !quote?.ok ? 'Fix the quote first' : ''}
            >
              {saving ? 'Creating…' : 'Create Draft Invoice'}
            </button>
          )}
          {isInvoice && draft && !sent && (
            <button onClick={handleSendInvoice} disabled={saving} style={{ ...btnPrimary, background: 'var(--accent3)' }}>
              {saving ? 'Sending…' : `Send Invoice to ${invoiceEmail}`}
            </button>
          )}
        </div>
        </>)}
      </div>
    </div>
  );
}

// What went out, after a prospect invite. Says plainly when the email did
// not send, and always offers the link — for an invite that is the INVITE
// link, which finishes their account; the cart link alone would strand them
// at a sign-in they cannot complete.
function InviteSent({ result, prospect, copied, onCopy, onDone }) {
  const outcome = sendOutcome(result, prospect?.email);
  const url = linkFor(result);
  const tone = outcome.ok ? 'var(--accent3)' : 'var(--badge-yellow)';
  return (
    <>
      <div style={body}>
        <div style={{ border: `1px solid ${tone}`, borderRadius: 12, padding: '18px 20px', background: 'var(--bg3)' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: tone, marginBottom: 6 }}>
            {outcome.ok ? (result.mode === 'invite' ? 'Account created' : 'Cart sent') : 'Needs you'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--text)', marginBottom: 6 }}>{outcome.headline}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{outcome.detail}</div>
          {url && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
              <input readOnly value={url} onFocus={e => e.target.select()} style={{ ...input, fontFamily: 'var(--mono)', fontSize: 11, flex: 1 }} />
              <button onClick={onCopy} style={btnSecondary}>{copied ? 'Copied' : (result.mode === 'invite' ? 'Copy invite link' : 'Copy cart link')}</button>
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 12 }}>
            It's on the Sales tab under Prospect invites, where you can resend it.
          </div>
        </div>
      </div>
      <div style={footer}>
        <button onClick={onDone} style={btnPrimary}>Done</button>
      </div>
    </>
  );
}

// ─── styles (inline — matches DnsSyncModal conventions) ─────────────────────
const backdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel = { width: '100%', maxWidth: 960, maxHeight: '90vh', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 20px', borderBottom: '1px solid var(--border)' };
const body = { padding: 20, overflowY: 'auto', flex: 1 };
const footer = { padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 };
const closeBtn = { background: 'none', border: 'none', color: 'var(--text3)', fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 0 };
const sectionLabel = { fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6, letterSpacing: '0.5px' };
const input = { width: '100%', padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', boxSizing: 'border-box' };
const productRow = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', marginBottom: 6 };
const addBtn = { padding: '4px 10px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--mono)' };
const emptyCart = { padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 12, border: '1px dashed var(--border)', borderRadius: 6 };
const cartRow = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, background: 'var(--bg3)', marginBottom: 6 };
const qtyInput = { width: 50, padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)', textAlign: 'center' };
const removeBtn = { background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 4, width: 24, height: 24, cursor: 'pointer', fontSize: 14, lineHeight: 1 };
const subtotalRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 10px', marginTop: 4, borderTop: '1px solid var(--border)' };
const btnPrimary = { padding: '8px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { padding: '8px 18px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const miniSelect = { padding: '4px 6px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 11, fontFamily: 'var(--mono)' };
const miniLabel = { display: 'block', fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 3 };
const modeBtn = { padding: '5px 12px', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--mono)' };
const modeBtnActive = { ...modeBtn, background: 'var(--bg3)', color: 'var(--text)', borderColor: 'var(--accent)' };
const errBox = { padding: 8, borderRadius: 5, border: '1px solid #a33', background: 'rgba(170,51,51,0.12)', color: 'var(--text2)', fontSize: 11, lineHeight: 1.45 };
const okBox = { marginTop: 10, padding: 8, borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text2)', fontSize: 11, lineHeight: 1.45 };
const warnBox = { padding: 8, borderRadius: 5, border: '1px dashed var(--border)', color: 'var(--text3)', fontSize: 10, lineHeight: 1.4, marginBottom: 8 };
